/**
 * Patient Information Section Component
 *
 * 受診者情報セクション - Patient basic information form section
 * Part of the Patient Edit screen (患者編集)
 */

import React, { useCallback, useEffect } from 'react';
import {
  UseFormRegister,
  UseFormWatch,
  UseFormSetValue,
  FieldErrors,
  Control,
} from 'react-hook-form';

import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import {
  PatientFormSchemaType,
  PATIENT_NAME_KANA_MAX_LENGTH,
  PatientFormData,
} from '@pages/main/patient/types';
import { usePatientInfoNormalization } from '@pages/main/patient/hooks';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { usePatientInfoHistoryMenu } from './PatientInfoHistoryMenu';
import { PatientInfoSectionForm } from './PatientInfoSectionForm';

interface PatientInfoSectionProps {
  register: UseFormRegister<PatientFormSchemaType>;
  control: Control<PatientFormSchemaType>;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  errors: FieldErrors<PatientFormSchemaType>;
  isEditMode: boolean;
  isCurrent: boolean;
  onAddressSearch: () => void;
  onOpenMap: () => void;
  /** Ref to 住所2 input — focused by AddressSearchModal after autofill. */
  address2Ref?: React.MutableRefObject<{ focus?: () => void } | null>;
  patientData?: PatientFormData | null;
  onHistorySwitch?: (type: 'patient_information', historyId: number) => void;
  onHistoryDelete?: (type: 'patient_information', historyId: number) => void;
  /** A2 edit.blade: 履歴作成取消 — exit new 受診者履歴 draft and reload current patient */
  onPatientHistoryCreateCancel?: () => void | Promise<void>;
  /**
   * A2: `patient_information_id` query present and empty (= new 受診者履歴 draft), same as Blade
   * `empty(@$patient_informations_data['patient_information_id'])` after URL sync.
   */
  patientInformationHistoryDraftByUrl?: boolean;
  disabled?: boolean;
}

const PatientInfoSection: React.FC<PatientInfoSectionProps> = ({
  control,
  watch,
  setValue,
  errors,
  isEditMode,
  isCurrent,
  onAddressSearch,
  onOpenMap,
  patientData,
  onHistorySwitch,
  onHistoryDelete,
  onPatientHistoryCreateCancel,
  patientInformationHistoryDraftByUrl = false,
  address2Ref,
  disabled = false,
}) => {
  // Get relationship ID from insurance card for name sync logic
  const relationshipId = watch('health_insurance_card.relationship_id');
  const patientNameKana = watch('patient_information.name_kana');

  const {
    nameInputProps: patientNameInputProps,
    kanaInputProps: patientNameKanaInputProps,
    maxLength: patientNameKanaMaxLength,
    syncLastKana,
  } = useJapaneseNameKanaFields({
    nameKanaValue: String(patientNameKana ?? ''),
    setNameValue: v =>
      setValue('patient_information.name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v =>
      setValue('patient_information.name_kana', v, { shouldDirty: true, shouldValidate: false }),
    maxLength: PATIENT_NAME_KANA_MAX_LENGTH,
    fullWidthAlphaNumOnBlur: false,
  });

  useEffect(() => {
    syncLastKana(String(patientNameKana ?? ''));
  }, [patientNameKana, syncLastKana]);

  // Use normalization hook to handle field sync and normalization
  const { getAge, syncPatientNameToInsuranceImmediate } = usePatientInfoNormalization({
    watch,
    setValue,
    relationshipId: relationshipId ?? undefined,
  });

  // Calculate age from date of birth
  const age = getAge();

  // Master data for lookups (sex options, relationships, etc.)
  const { data: masterData } = usePatientMasterData();
  const sexOptions = masterData?.sexes || [];

  // Sync patient name to insurance card on blur (covers clearing to empty)
  const handlePatientNameBlur = useCallback(() => {
    const name = watch('patient_information.name') || '';
    const nameKana = watch('patient_information.name_kana') || '';
    syncPatientNameToInsuranceImmediate(name, nameKana);
  }, [watch, syncPatientNameToInsuranceImmediate]);

  const patientInformationRowId = watch('patient_information.id');
  const { historyMenu, showPatientHistoryCreateCancel } = usePatientInfoHistoryMenu({
    isEditMode,
    isCurrent,
    disabled,
    patientData,
    patientInformationRowId,
    patientInformationHistoryDraftByUrl,
    onHistorySwitch,
    onHistoryDelete,
    onPatientHistoryCreateCancel,
  });

  return (
    <CollapsibleCard
      title={`受診者${!isCurrent && isEditMode ? '（履歴表示）' : ''}`}
      extraHeaderContent={historyMenu}
      headerBackgroundColor={
        showPatientHistoryCreateCancel
          ? '#000'
          : !isCurrent && isEditMode
            ? '#6b7280'
            : undefined
      }
    >
      {/* Helper to block inputs when disabled prop is true (history creation for other sections) */}
      <div style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
        <PatientInfoSectionForm
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={disabled}
          age={age}
          sexOptions={sexOptions}
          patientNameKanaMaxLength={patientNameKanaMaxLength}
          patientNameInputProps={patientNameInputProps}
          patientNameKanaInputProps={patientNameKanaInputProps}
          onAddressSearch={onAddressSearch}
          onOpenMap={onOpenMap}
          onPatientNameBlur={handlePatientNameBlur}
          address2Ref={address2Ref}
        />
      </div>
    </CollapsibleCard>
  );
};

export default PatientInfoSection;
